import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import CustomPhoneInput from "../../components/common/CustomPhoneInput";
import { logo } from "../../constants/icons";
import {
  agentTenderService,
  agentTenderPublicService,
} from "../../services/agentTenderApi";
import {
  formatPositionValue,
  formatStaffRole,
  formatTypeOfTender,
} from "../../constants/agentTenderChoices";

// Public, mobile-first verification page for a club contact (e.g. a sporting
// director). No login needed. The signed token in the URL is the credential.
// Presentation rebuilt 1:1 from the design handoff (doc 03, screens 9 + 9b;
// UI REBUILD 2026-07-22): #F8FAFC page, 56px white top bar with the full
// TransferPitch logo at 32px left (LOGO export placement), 520px content
// column, artboard SVGs verbatim (Rule 2).
//
// V2 state machine (design doc 03, screens 9 + 9b):
//   loading → oneclick             (signed-in member of THIS club: no code)
//           → summary → identity → otp → result   (logged-out contact)
// Decline is reachable from every confirm step; a fraud report sheet is
// reachable from EVERY club-facing step. The club contact enters THEIR OWN
// phone number; the agent can never prefill it (Brief Rule 3). The V2
// identity step records WHO confirms (name + role) in the audit trail.

// ── Artboard SVGs (doc 03 — verbatim) ────────────────────────────────────────
const IconShieldCheck = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2l7 3v6c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V5z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const IconCheck = ({ size = 15, stroke = "currentColor", strokeWidth = "2.6" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12l5 5L20 7" />
  </svg>
);
const IconDownload = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v12M7 10l5 5 5-5" />
    <path d="M4 21h16" />
  </svg>
);
const IconFlag = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 15V4a1 1 0 0 1 1-1c3 0 5 2 8 2s4-1 6-1v11c-2 0-3 1-6 1s-5-2-8-2a1 1 0 0 0-1 1z" />
    <path d="M4 22v-7" />
  </svg>
);
const IconChevronDown = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const RESULT_TEXT = {
  declined: {
    title: "Request declined",
    body:
      'The agent sees the decline and your reason on the journey row ("Try again" stays possible). You can close this page.',
  },
  expired: {
    title: "Link expired",
    body:
      "This verification link is no longer valid. Please ask the agent to send a new one.",
  },
  lockout: {
    title: "Too many attempts",
    body:
      "For your security, verification is paused for a short while. Please try again later.",
  },
  error: {
    title: "Something went wrong",
    body:
      "We couldn't complete the verification. Please try again, or ask the agent to resend the link.",
  },
};

// Identity-step role choices (design 9b step 2). "Other" opens a write-in.
const CONFIRMER_ROLES = [
  "Sporting Director",
  "Technical Director",
  "Board member",
  "Head of Recruitment",
  "Other",
];

// Page shell (artboard): #F8FAFC page, 56px white top bar with the full logo
// at 32px height, left (LOGO export placement, all club-facing steps), then
// a 520px content column.
const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#F8FAFC]">
    <div className="h-14 bg-white border-b border-[#E2E8F0] flex items-center px-4">
      <img src={logo} alt="TransferPitch" className="h-8 w-auto" />
    </div>
    <div className="max-w-[520px] mx-auto px-4 py-5 sm:py-9 flex flex-col gap-[13px]">
      {children}
    </div>
  </div>
);

const Title = ({ children, center = false }) => (
  <h1
    className={`text-[19px] sm:text-[22px] font-extrabold tracking-tight text-[#0F172A] ${
      center ? "sm:text-center" : ""
    }`}
  >
    {children}
  </h1>
);

// One-tap fraud entry (V2): present on every club-facing step.
const FraudButton = ({ onClick, divider = false }) => (
  <div
    className={`flex justify-center ${
      divider ? "border-t border-[#F1F5F9] pt-[11px]" : ""
    }`}
  >
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-[9px] border border-[#FECACA] bg-white text-[#B91C1C] text-xs font-semibold hover:bg-red-50"
    >
      <IconFlag />
      Report fraud
    </button>
  </div>
);

const primaryBtnBase =
  "w-full h-12 rounded-[11px] text-sm font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50";
const secondaryBtn =
  "w-full h-11 rounded-[11px] text-[13px] font-semibold border border-[#E2E8F0] bg-white text-[#334155] hover:border-gray-300 inline-flex items-center justify-center";

// "Jul 21, 2026, 14:41" (artboard done-screen timestamp format).
const fmtConfirmedAt = (d) =>
  `${d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;

// Confirmed result (both paths): one-click AND the logged-out step-4 done
// screen. Carries the confirmer line + the certificate download.
const VerifiedScreen = ({ confirmer, certificateUrl, alreadyConfirmed, clubName, confirmedAt }) => (
  <Shell>
    <div className="flex flex-col items-center text-center gap-3.5 pt-2">
      <span className="w-16 h-16 rounded-full bg-[#ECFDF5] inline-flex items-center justify-center">
        <IconShieldCheck />
      </span>
      <h1 className="text-xl font-extrabold tracking-tight text-[#0F172A]">
        {alreadyConfirmed ? "Already confirmed" : "Request verified"}
      </h1>
      {alreadyConfirmed ? (
        <p className="text-[12.5px] text-[#475569] leading-relaxed">
          This request has already been confirmed. No further action is needed.
        </p>
      ) : (
        <>
          {(confirmer?.name || confirmer?.role) && (
            <p className="text-[12.5px] text-[#475569]">
              Confirmed by{" "}
              <strong>
                {[confirmer.name, confirmer.role].filter(Boolean).join(" · ")}
              </strong>
              {(clubName || confirmedAt) && (
                <>
                  <br />
                  {[clubName, confirmedAt ? fmtConfirmedAt(confirmedAt) : ""]
                    .filter(Boolean)
                    .join(" · ")}
                </>
              )}
            </p>
          )}
          <div className="self-stretch text-left bg-white border border-[#EEF2F7] rounded-[11px] px-3.5 py-3 text-xs text-[#64748B] leading-relaxed">
            The request now carries the Club Verified badge and ranks first in
            the marketplace. {clubName || "Your club"} stays anonymous there ·
            this confirmation is not shown publicly with your name.
          </div>
          {/* V-2 (Marin 2026-07-22): withdraw right restored as a muted
              footnote on BOTH confirmation success states (this shared screen
              renders the logged-out done screen AND the one-click result). */}
          <p className="text-[11.5px] text-[#94A3B8]">
            You can withdraw this at any time by contacting us.
          </p>
        </>
      )}
      {certificateUrl && (
        <a
          href={certificateUrl}
          download
          className="self-stretch h-11 rounded-[11px] border border-[#A7F3D0] bg-white text-[#047857] text-[13px] font-semibold inline-flex items-center justify-center gap-[7px] hover:bg-emerald-50"
        >
          <IconDownload />
          Download confirmation (PDF)
        </a>
      )}
      <p className="text-[11.5px] text-[#94A3B8]">
        Optional · you can simply close this page. Nothing to install, no
        account created.
      </p>
    </div>
  </Shell>
);

const ResultScreen = ({ kind, onReportFraud }) => {
  const t = RESULT_TEXT[kind] || RESULT_TEXT.error;
  const declined = kind === "declined";
  return (
    <Shell>
      <div className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-3.5 flex gap-2.5 items-start mt-1">
        <span
          className={`flex-none w-[26px] h-[26px] rounded-full inline-flex items-center justify-center text-[13px] font-extrabold ${
            declined ? "bg-[#FEE2E2] text-[#B91C1C]" : "bg-[#F1F5F9] text-[#475569]"
          }`}
          aria-hidden="true"
        >
          !
        </span>
        <div>
          <h1 className="text-[13px] font-bold text-[#0F172A]">{t.title}</h1>
          <p className="text-[11.5px] text-[#64748B] mt-0.5 leading-relaxed">{t.body}</p>
        </div>
      </div>
      {declined && onReportFraud && <FraudButton onClick={onReportFraud} />}
    </Shell>
  );
};

const AgentTenderVerifyPublic = () => {
  const { token } = useParams();
  const location = useLocation();
  // Channel tag ("link" | "qr") from the shared URL — forwarded to every public
  // call so the backend logs how the club arrived. Not consumed by the page.
  const channel = new URLSearchParams(location.search).get("c") || undefined;
  const [step, setStep] = useState("loading"); // loading|oneclick|summary|identity|otp|decline|<result>
  const [summary, setSummary] = useState(null);
  // signed_in_member: {is_club_member,name,role,club_name,tender_id} | null —
  // set only when the viewer is a signed-in member of THIS request's club.
  const [member, setMember] = useState(null);
  const [fullName, setFullName] = useState("");
  const [roleChoice, setRoleChoice] = useState("");
  const [roleOther, setRoleOther] = useState("");
  const [phone, setPhone] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState("");
  const [identityErrors, setIdentityErrors] = useState({});
  // Stamped the moment the confirmation succeeds (done-screen timestamp line).
  const [confirmedAt, setConfirmedAt] = useState(null);
  // Fraud sheet overlays whatever step is active. "sent" flips it to the
  // confirmation body; fraudError carries the throttle/notice line.
  const [fraudOpen, setFraudOpen] = useState(false);
  const [fraudNote, setFraudNote] = useState("");
  const [fraudSent, setFraudSent] = useState(false);
  const [fraudError, setFraudError] = useState("");

  const confirmerRole = roleChoice === "Other" ? roleOther.trim() : roleChoice;
  const identity = {
    confirmer_name: member ? member.name : fullName.trim(),
    confirmer_role: member ? member.role : confirmerRole,
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await agentTenderPublicService.getVerify(token, channel);
      if (cancelled) return;
      if (res.success) {
        setSummary(res.data);
        // One-click confirm state (screen 9) for a signed-in club member.
        if (res.data?.signed_in_member?.is_club_member) {
          setMember(res.data.signed_in_member);
          setStep("oneclick");
        } else {
          setStep("summary");
        }
      } else if (res.status === 410) {
        setStep("expired");
      } else {
        setStep("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, channel]);

  const submitIdentityAndPhone = async () => {
    if (busy) return;
    // Identity step (V2): full name + role are REQUIRED — the confirmation
    // records WHO confirmed, next to channel and timestamp.
    const errs = {};
    if (!fullName.trim()) errs.name = "Please enter your full name.";
    if (!roleChoice) errs.role = "Please select your role at the club.";
    if (roleChoice === "Other" && !roleOther.trim())
      errs.role = "Please write your role at the club.";
    setIdentityErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setFieldError("");
    setBusy(true);
    const res = await agentTenderPublicService.submitPhone(token, phone, channel, identity);
    setBusy(false);
    if (res.success) {
      setMaskedPhone(res.data?.masked_phone || "");
      setStep("otp");
    } else if (res.status === 429) {
      setStep("lockout");
    } else {
      setFieldError(res.error || "Please enter a valid phone number.");
    }
  };

  const submitOtp = async () => {
    if (busy) return;
    setFieldError("");
    setBusy(true);
    const res = await agentTenderPublicService.confirmOtp(token, otp, channel, identity);
    setBusy(false);
    if (res.success) {
      setConfirmedAt(new Date());
      setStep("success");
    } else if (res.status === 429) {
      setStep("lockout");
    } else if (res.status === 409) {
      setStep("already_verified");
    } else if (res.status === 410) {
      setStep("expired");
    } else {
      setFieldError(res.error || "That code isn't correct. Please check and try again.");
    }
  };

  // One-click confirm: the session IS the identity — authed endpoint, no OTP.
  const submitOneClick = async () => {
    if (busy) return;
    setBusy(true);
    const res = await agentTenderService.confirmInPlatform(member.tender_id);
    setBusy(false);
    if (res.success) {
      setConfirmedAt(new Date());
      setStep("success");
    } else if (res.status === 409) {
      setStep("already_verified");
    } else {
      setFieldError(res.error || "We couldn't confirm the request. Please try again.");
    }
  };

  const submitDecline = async () => {
    if (busy) return;
    setBusy(true);
    const res = await agentTenderPublicService.decline(token, reason, channel);
    setBusy(false);
    setStep(res.success ? "declined" : "error");
  };

  const submitFraud = async () => {
    if (busy) return;
    setFraudError("");
    setBusy(true);
    const res = await agentTenderPublicService.reportFraud(
      token,
      {
        note: fraudNote,
        reporter_name: identity.confirmer_name,
        reporter_role: identity.confirmer_role,
      },
      channel,
    );
    setBusy(false);
    if (res.success) {
      setFraudSent(true);
    } else if (res.status === 429) {
      setFraudError("You've sent several reports recently. Please try again later.");
    } else {
      setFraudError(res.error || "We couldn't send the report. Please try again.");
    }
  };

  const openFraud = () => {
    setFraudError("");
    setFraudOpen(true);
  };

  const agentName = summary?.responsible_agent?.display_name || "";
  const agencyName = summary?.posted_agency?.name || "";
  // Item 4: real club name from the public verify payload (BE 2026-07-22
  // follow-up). This page is only ever seen by the club contact themselves,
  // so naming the club here breaks no anonymity rule; older payloads without
  // the field fall back to the "your club" copy everywhere.
  const clubName = summary?.club_name || "";
  const tenderTitle = (() => {
    const t = summary?.tender || {};
    // Staff requests title with the staff role (custom title for "other"),
    // player requests with the position — same weiche as Card/Loupe (F2).
    return t.tender_category === "staff"
      ? formatStaffRole(t.staff_role, t.other_staff_position)
      : formatPositionValue(t.position);
  })();
  const tenderLabel = [tenderTitle, formatTypeOfTender(summary?.tender?.type_of_tender)]
    .filter(Boolean)
    .join(" · ");
  const postedBy = [agentName, agencyName].filter(Boolean).join(" · ");

  // Fraud sheet — one tap, nothing to type; poster/channel/history attach
  // server-side. Rendered above whatever step is active. Artboard: bottom
  // sheet (mobile) with the receipt-style context card.
  const fraudReceiptRow = (label, value, extra = "") => (
    <div className={`flex gap-2 ${extra}`}>
      <span className="flex-none w-[70px] text-[#94A3B8] font-bold">{label}</span>
      <span>{value}</span>
    </div>
  );
  const fraudSheet = fraudOpen && (
    <div className="fixed inset-0 z-50 bg-[rgba(15,23,42,0.42)] flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-[18px] sm:rounded-2xl w-full sm:max-w-md px-4 pb-5 pt-2.5 sm:p-6 shadow-[0_-12px_40px_rgba(15,23,42,0.25)] flex flex-col gap-3">
        <div className="flex justify-center sm:hidden">
          <span className="w-9 h-1 rounded-full bg-[#E2E8F0]" />
        </div>
        {fraudSent ? (
          <>
            <div className="flex items-start gap-2.5">
              <span className="flex-none w-[26px] h-[26px] rounded-full bg-[#ECFDF5] text-[#047857] inline-flex items-center justify-center">
                <IconCheck size={13} />
              </span>
              <div>
                <h3 className="text-[13px] font-bold text-[#0F172A]">Report sent</h3>
                <p className="text-[11.5px] text-[#64748B] mt-0.5 leading-relaxed">
                  Our trust team reviews every report. Proven fraud removes the
                  request and permanently bans the poster. You don't need to do
                  anything else.
                </p>
              </div>
            </div>
            <button
              onClick={() => setFraudOpen(false)}
              className="h-[42px] border border-[#E2E8F0] rounded-[11px] text-[13px] font-semibold text-[#334155] hover:bg-gray-50"
            >
              Close
            </button>
          </>
        ) : (
          <>
            <h3 className="text-[17px] font-extrabold tracking-tight text-[#0F172A]">
              Report this request as fraud?
            </h3>
            <p className="text-xs text-[#64748B]">
              Nothing to type · we already have the context. This is what we
              receive:
            </p>
            <div className="bg-[#F8FAFC] border border-[#EEF2F7] rounded-[11px] px-3.5 py-3 text-xs text-[#334155] flex flex-col gap-[7px]">
              {fraudReceiptRow(
                "Report",
                <span className="font-semibold">
                  Fraud{summary?.tender?.id ? ` · REQ-${summary.tender.id}` : ""}
                </span>,
              )}
              {postedBy &&
                fraudReceiptRow(
                  "About",
                  <span className="font-semibold">{postedBy} (poster)</span>,
                )}
              {tenderLabel &&
                fraudReceiptRow(
                  "Request",
                  <span>
                    {tenderLabel}
                    {clubName ? ` · claimed for ${clubName}` : ""}
                  </span>,
                )}
              {(identity.confirmer_name || identity.confirmer_role) &&
                fraudReceiptRow(
                  "Reported by",
                  <span>
                    {[identity.confirmer_name, identity.confirmer_role]
                      .filter(Boolean)
                      .join(" · ")}
                    {" (link recipient)"}
                  </span>,
                )}
              {fraudReceiptRow(
                "Attached",
                <span className="text-[#64748B]">
                  Channel (QR/link), open + confirm history, timestamps ·
                  automatic
                </span>,
                "border-t border-dashed border-[#E2E8F0] pt-[7px]",
              )}
            </div>
            <textarea
              rows={2}
              value={fraudNote}
              onChange={(e) => setFraudNote(e.target.value)}
              placeholder="Anything we should know? (optional)"
              className="w-full min-h-[56px] px-3 py-2.5 border border-[#E2E8F0] rounded-[9px] text-[12.5px] placeholder-[#94A3B8] focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {fraudError && <p className="text-xs text-red-500">{fraudError}</p>}
            <button
              onClick={submitFraud}
              disabled={busy}
              className={`${primaryBtnBase} bg-[#B91C1C] hover:bg-red-800`}
            >
              <IconFlag size={13} />
              {busy ? "Sending…" : "Send fraud report"}
            </button>
            <button
              onClick={() => setFraudOpen(false)}
              className="h-[42px] border border-[#E2E8F0] rounded-[11px] text-[13px] font-semibold text-[#334155] hover:bg-gray-50"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );

  if (step === "success" || step === "already_verified") {
    return (
      <>
        <VerifiedScreen
          confirmer={{ name: identity.confirmer_name, role: identity.confirmer_role }}
          certificateUrl={agentTenderPublicService.certificateUrl(token)}
          alreadyConfirmed={step === "already_verified"}
          clubName={clubName || member?.club_name || ""}
          confirmedAt={confirmedAt}
        />
        {fraudSheet}
      </>
    );
  }

  if (["declined", "expired", "lockout", "error"].includes(step)) {
    return (
      <>
        <ResultScreen
          kind={step}
          onReportFraud={step === "declined" ? openFraud : undefined}
        />
        {fraudSheet}
      </>
    );
  }

  if (step === "loading") {
    return (
      <Shell>
        <p className="text-center text-gray-500">Loading…</p>
      </Shell>
    );
  }

  // Shared request facts card (artboard: heading + type/level pills, Period /
  // Posted by rows, anonymity note). The commission row is kept from the
  // functional V2 build (BE #163 three-state) in the same row grammar — the
  // artboard is silent about it.
  const factsCard = (withLabel = false) => {
    const t = summary?.tender || {};
    const rows = [
      ["Period", t.period],
      [
        "Agent commission split",
        // Commission three-state: explicit no-commission wins, then the
        // split (with optional note), then Negotiable.
        t.no_commission
          ? "No commission for this job"
          : t.commission_split || t.commission_split_note || "Negotiable",
      ],
      ["Posted by", postedBy],
    ].filter(([, v]) => v);
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex flex-col gap-2.5">
        {withLabel && (
          <div className="text-[11px] font-extrabold tracking-[0.05em] text-[#94A3B8] uppercase">
            The request
          </div>
        )}
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[17px] font-extrabold text-[#0F172A]">
            {tenderTitle}
          </span>
          {t.type_of_tender && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#E8F1FF] text-[#1B6FE0]">
              {formatTypeOfTender(t.type_of_tender)}
            </span>
          )}
          {t.recruiting_for && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#475569]">
              {formatTypeOfTender(t.recruiting_for)}
            </span>
          )}
        </div>
        {rows.length > 0 && (
          <div className="flex flex-col gap-1.5 text-[12.5px] text-[#334155]">
            {rows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3">
                <span className="text-[#64748B]">{label}</span>
                <span className="font-semibold text-right">{value}</span>
              </div>
            ))}
          </div>
        )}
        <div className="text-[11.5px] text-[#94A3B8] border-t border-dashed border-[#EEF2F7] pt-[9px]">
          In the marketplace this request never shows your club. Only you see
          this page.
        </div>
      </div>
    );
  };

  // ── One-click confirm (screen 9): signed-in member of the request's club ──
  if (step === "oneclick") {
    return (
      <>
        <Shell>
          <Title center>Confirm agent request</Title>
          <div className="flex items-center sm:justify-center gap-2.5 rounded-[11px] bg-[#ECFDF5] border border-[#A7F3D0] px-3 py-[11px]">
            <span className="flex-none w-7 h-7 rounded-full bg-[#047857] text-white text-[10.5px] font-extrabold inline-flex items-center justify-center">
              {(member.name || "?")
                .split(/\s+/)
                .map((p) => p[0])
                .slice(0, 2)
                .join("")
                .toUpperCase()}
            </span>
            <p className="text-xs sm:text-[12.5px] text-[#065F46] leading-snug">
              <strong>Signed in as {member.name}</strong>
              <br className="sm:hidden" />
              <span className="hidden sm:inline"> · </span>
              {[member.role, member.club_name].filter(Boolean).join(" · ")}
              {" · "}no code needed
            </p>
          </div>
          {factsCard(true)}
          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              onClick={submitOneClick}
              disabled={busy}
              className={`${primaryBtnBase} sm:flex-1 bg-[#047857] hover:bg-emerald-800`}
            >
              <IconCheck />
              {busy ? "Confirming…" : "Confirm: this is our request"}
            </button>
            <button
              onClick={() => setStep("decline")}
              className={`${secondaryBtn} sm:w-auto sm:h-12 sm:px-[18px]`}
            >
              This isn't ours
            </button>
          </div>
          {fieldError && (
            <p className="text-sm text-red-500 text-center">{fieldError}</p>
          )}
          <p className="text-center text-[11.5px] text-[#94A3B8]">
            Confirming means {clubName || member?.club_name || "your club"}{" "}
            recognizes this need and this agent as its channel on the platform.
            Nothing more.
          </p>
          <FraudButton onClick={openFraud} />
        </Shell>
        {fraudSheet}
      </>
    );
  }

  if (step === "summary") {
    return (
      <>
        <Shell>
          <Title>Confirm agent request</Title>
          <p className="text-[12.5px] text-[#64748B]">
            {agentName || "An agent"}
            {agencyName ? ` of ${agencyName}` : ""} asks{" "}
            {clubName || "your club"} to confirm this request.
          </p>
          {factsCard()}
          <button
            onClick={() => setStep("identity")}
            className={`${primaryBtnBase} bg-[#2283FF] hover:bg-blue-600`}
          >
            This is our request
          </button>
          <button onClick={() => setStep("decline")} className={secondaryBtn}>
            This isn't ours
          </button>
          <p className="text-center text-[11.5px] text-[#94A3B8]">
            Signed in as a member of this club? You'll get the one-click
            confirm instead of the code.
          </p>
          <FraudButton onClick={openFraud} divider />
        </Shell>
        {fraudSheet}
      </>
    );
  }

  // ── Identity step (V2, design 9b step 2): who confirms + own phone ────────
  if (step === "identity") {
    return (
      <>
        <Shell>
          <Title>Before you confirm</Title>
          <p className="text-[12.5px] text-[#64748B]">
            Tell us who is confirming for {clubName || "your club"}. This is
            recorded with the confirmation.
          </p>
          <div>
            <label className="block text-[12.5px] font-semibold text-[#334155] mb-1.5">
              Your full name <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                if (identityErrors.name)
                  setIdentityErrors((prev) => ({ ...prev, name: undefined }));
              }}
              placeholder="First and last name"
              className={`w-full h-11 px-3 border rounded-[9px] text-[13.5px] focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                identityErrors.name ? "border-red-500" : "border-[#E2E8F0]"
              }`}
            />
            {identityErrors.name && (
              <p className="text-xs text-red-500 mt-1">{identityErrors.name}</p>
            )}
          </div>
          <div>
            <label className="block text-[12.5px] font-semibold text-[#334155] mb-1.5">
              Your role at the club <span className="text-[#DC2626]">*</span>
            </label>
            <div className="relative">
              <select
                value={roleChoice}
                onChange={(e) => {
                  setRoleChoice(e.target.value);
                  if (identityErrors.role)
                    setIdentityErrors((prev) => ({ ...prev, role: undefined }));
                }}
                className={`w-full h-11 px-3 pr-8 border rounded-[9px] text-[13.5px] bg-white appearance-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  identityErrors.role ? "border-red-500" : "border-[#E2E8F0]"
                }`}
              >
                <option value="">Select your role…</option>
                {CONFIRMER_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r === "Other" ? "Other (write it)" : r}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                <IconChevronDown />
              </span>
            </div>
            <p className="text-[11.5px] text-[#94A3B8] mt-[5px]">
              Sporting Director · Technical Director · Board member · Head of
              Recruitment · Other (write it)
            </p>
            {roleChoice === "Other" && (
              <input
                type="text"
                value={roleOther}
                onChange={(e) => {
                  setRoleOther(e.target.value);
                  if (identityErrors.role)
                    setIdentityErrors((prev) => ({ ...prev, role: undefined }));
                }}
                placeholder="Your role at the club"
                className="w-full mt-2 h-11 px-3 border border-[#E2E8F0] rounded-[9px] text-[13.5px] focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            )}
            {identityErrors.role && (
              <p className="text-xs text-red-500 mt-1">{identityErrors.role}</p>
            )}
          </div>
          <div>
            <CustomPhoneInput
              label="Your mobile number"
              value={phone}
              onChange={(v) => setPhone(v)}
              required
              error={fieldError}
            />
            <p className="text-[11.5px] text-[#94A3B8] mt-[5px]">
              We text a 6-digit code to this number. You enter it yourself ·
              the agent never sees or provides it.
            </p>
          </div>
          <button
            onClick={submitIdentityAndPhone}
            disabled={busy}
            className={`${primaryBtnBase} bg-[#2283FF] hover:bg-blue-600`}
          >
            {busy ? "Sending…" : "Send me the code"}
          </button>
          <button
            onClick={() => {
              setFieldError("");
              setStep("summary");
            }}
            className="w-full py-1 text-[#64748B] text-[13px] font-medium hover:text-gray-900"
          >
            Back
          </button>
          <FraudButton onClick={openFraud} />
        </Shell>
        {fraudSheet}
      </>
    );
  }

  if (step === "otp") {
    // Six code boxes (artboard 9b step 3): the real input overlays the row,
    // invisible — one string state, boxes are pure presentation.
    const digits = Array.from({ length: 6 }, (_, i) => otp[i] || "");
    return (
      <>
        <Shell>
          <Title>Enter the code</Title>
          <p className="text-[12.5px] text-[#64748B]">
            Sent by SMS to {maskedPhone || "your phone"}
          </p>
          <div className="relative py-1.5">
            <div className="flex gap-2 justify-center" aria-hidden="true">
              {digits.map((d, i) => (
                <span
                  key={i}
                  className={`w-[46px] h-[54px] rounded-[10px] bg-white inline-flex items-center justify-center text-xl font-extrabold ${
                    d
                      ? "border-[1.5px] border-[#2283FF]"
                      : "border border-[#E2E8F0]"
                  }`}
                >
                  {d}
                </span>
              ))}
            </div>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              aria-label="Verification code"
              className="absolute inset-0 w-full h-full opacity-0 cursor-text"
            />
          </div>
          {fieldError && (
            <p className="text-sm text-red-500 text-center">{fieldError}</p>
          )}
          <button
            onClick={submitOtp}
            disabled={busy}
            className={`${primaryBtnBase} bg-[#047857] hover:bg-emerald-800`}
          >
            <IconCheck />
            {busy ? "Confirming…" : "Confirm: this is our request"}
          </button>
          <div className="flex justify-between items-center text-xs">
            <button
              onClick={submitIdentityAndPhone}
              disabled={busy}
              className="text-[#1B6FE0] font-semibold hover:underline disabled:opacity-50"
            >
              Resend code
            </button>
            <span className="text-[#94A3B8]">5 attempts, then 15 min pause</span>
          </div>
          <button
            onClick={() => setStep("decline")}
            className="w-full h-10 rounded-[9px] border border-[#E2E8F0] bg-white text-[#334155] text-[12.5px] font-semibold hover:border-gray-300"
          >
            This isn't ours
          </button>
          <FraudButton onClick={openFraud} />
        </Shell>
        {fraudSheet}
      </>
    );
  }

  if (step === "decline") {
    return (
      <>
        <Shell>
          <Title>This isn't ours?</Title>
          <p className="text-[12.5px] text-[#64748B]">
            Declining tells the agent this request is not confirmed by{" "}
            {clubName || "your club"}. A reason helps, but is optional.
          </p>
          <div>
            <label className="block text-[12.5px] font-semibold text-[#334155] mb-1.5">
              Reason{" "}
              <span className="font-medium text-[#94A3B8]">
                (optional, max 500 characters)
              </span>
            </label>
            <textarea
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Tell us what's wrong (optional)"
              className="w-full min-h-[84px] px-3 py-2.5 border border-[#E2E8F0] rounded-[9px] text-[13px] text-[#334155] placeholder-[#94A3B8] focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={submitDecline}
            disabled={busy}
            className={`${primaryBtnBase} bg-[#B91C1C] hover:bg-red-800`}
          >
            {busy ? "Submitting…" : "Decline request"}
          </button>
          <button
            onClick={() => setStep(member ? "oneclick" : "summary")}
            className={secondaryBtn}
          >
            Back
          </button>
          <FraudButton onClick={openFraud} />
        </Shell>
        {fraudSheet}
      </>
    );
  }

  return (
    <Shell>
      <p className="text-center text-gray-500">Loading…</p>
    </Shell>
  );
};

export default AgentTenderVerifyPublic;
